import React, { useState } from 'react';
import { graphql, useFragment } from 'react-relay';
import EditorPanelIcon from '@atlaskit/icon/core/migration/information--editor-panel';
import { Box, xcss, Inline } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { ContextualAnalyticsData, SCREEN } from '@atlassian/jira-product-analytics-bridge';
import type { projectNotificationsConfigTable_personalSettingsNotificationsConfigTable_ProjectNotificationConfigTableNew_enabledPreferenceFragment$key } from '@atlassian/jira-relay/src/__generated__/projectNotificationsConfigTable_personalSettingsNotificationsConfigTable_ProjectNotificationConfigTableNew_enabledPreferenceFragment.graphql';
import type { projectNotificationsConfigTable_personalSettingsNotificationsConfigTable_ProjectNotificationConfigTableNew_projectPreferencesData$key } from '@atlassian/jira-relay/src/__generated__/projectNotificationsConfigTable_personalSettingsNotificationsConfigTable_ProjectNotificationConfigTableNew_projectPreferencesData.graphql';
import { fg } from '@atlassian/jira-feature-gating';
import { EMAIL, IN_PRODUCT } from '../../../common/constants.tsx';
import type { HeadCheckboxIdsType } from '../../../common/types.tsx';
import messages from '../../../common/ui/messages.tsx';
import { NotificationsConfigRow as NotificationsConfigRowAccordion } from '../../../common/ui/notifications-accordian-row/index.tsx';
import { HeadCheckbox } from '../../../common/ui/notifications-config-head/index.tsx';
import { HeadCellCheckboxContainer } from '../../../common/ui/notifications-config-head/styled.tsx';
import { NotificationsConfigRow } from '../../../common/ui/notifications-config-row/index.tsx';
import {
	Table,
	TableHeadContainer,
	TableHeadRowContainer,
	HeadCellContainer,
	TBody,
} from '../../../common/ui/styled.tsx';
import { ProjectNotificationsAccordion } from './project-notifications-accordion/index.tsx';
import { useProjectHeadMutation } from './project-notifications-config-head/index.tsx';
import { useProjectRowMutation } from './project-notifications-config-row/index.tsx';
import { RemoveButton } from './project-notifications-remove-button/index.tsx';

export const ProjectNotificationConfigTableNew = ({
	projectPreferencesData,
	enabledPreferenceFragment,
}: {
	projectPreferencesData: projectNotificationsConfigTable_personalSettingsNotificationsConfigTable_ProjectNotificationConfigTableNew_projectPreferencesData$key;
	enabledPreferenceFragment: projectNotificationsConfigTable_personalSettingsNotificationsConfigTable_ProjectNotificationConfigTableNew_enabledPreferenceFragment$key;
}) => {
	const { formatMessage } = useIntl();
	const [isInfoPanelOpen, setIsInfoPanelOpen] = useState(false);
	const enabledPreference = useFragment(
		graphql`
			fragment projectNotificationsConfigTable_personalSettingsNotificationsConfigTable_ProjectNotificationConfigTableNew_enabledPreferenceFragment on Query {
				jira @required(action: THROW) {
					notificationGlobalPreference(cloudId: $cloudId)
						@required(action: THROW)
						@optIn(to: "JiraNotificationPreference") {
						...notificationsConfigHead_personalSettingsNotificationsConfigTable_HeadCheckbox_enabledDataFragment
						...notificationsConfigRow_personalSettingsNotificationsConfigTable_enabledDataFragment
						...notificationsAccordianRow_personalSettingsNotificationsConfigTable_NotificationsConfigRow_enabledDataFragment
					}
				}
			}
		`,
		enabledPreferenceFragment,
	);
	const projectPreferences = useFragment(
		graphql`
			fragment projectNotificationsConfigTable_personalSettingsNotificationsConfigTable_ProjectNotificationConfigTableNew_projectPreferencesData on JiraNotificationProjectPreferences {
				...projectNotificationsRemoveButton_personalSettingsNotificationsConfigTable_RemoveButton

				project @required(action: THROW) {
					id @required(action: THROW)
					...projectNotificationsAccordion_personalSettingsNotificationsConfigTable
				}
				preferences @required(action: THROW) {
					...notificationsConfigHead_personalSettingsNotificationsConfigTable_HeadCheckbox_allPreferences
					...notificationsAccordianRow_personalSettingsNotificationsConfigTable_NotificationsConfigRow_allPreferences
					...notificationsConfigRow_personalSettingsNotificationsConfigTable_allPreferences
					issueAssigned @required(action: THROW) {
						...notificationsConfigRow_personalSettingsNotificationsConfigTable_preference
					}
					mentionsCombined @required(action: THROW) {
						...notificationsConfigRow_personalSettingsNotificationsConfigTable_preference
					}
					worklogCombined @required(action: THROW) {
						...notificationsConfigRow_personalSettingsNotificationsConfigTable_preference
					}
					miscellaneousIssueEventCombined @required(action: THROW) {
						...notificationsConfigRow_personalSettingsNotificationsConfigTable_preference
					}
				}
			}
		`,
		projectPreferencesData,
	);
	const allPreferences = projectPreferences.preferences;
	const { project } = projectPreferences;
	const projectId = project?.id;
	const projectHeadMutationInProduct = useProjectHeadMutation({
		projectId,
		channel: IN_PRODUCT,
	});
	const projectHeadMutationEmail = useProjectHeadMutation({
		projectId,
		channel: EMAIL,
	});
	const projectRowMutation = useProjectRowMutation({ projectId });

	const emailProjectHeadCheckboxId = `email-${projectId}`;
	const inProductProjectHeadCheckboxId = `inProduct-${projectId}`;

	const headCheckboxIds: HeadCheckboxIdsType = {
		EMAIL: emailProjectHeadCheckboxId,
		IN_PRODUCT: inProductProjectHeadCheckboxId,
	};

	if (!allPreferences || !projectId) {
		return null;
	}
	return (
		<ContextualAnalyticsData
			sourceName="personalSettingsProjectNotifications"
			sourceType={SCREEN}
			attributes={{ projectId }}
		>
			<Box xcss={projectContainerStyles}>
				<ProjectNotificationsAccordion project={project}>
					<Box xcss={tableWrapperStyles}>
						<Table>
							<TableHeadContainer>
								<TableHeadRowContainer>
									<HeadCellContainer>
										{formatMessage(
											fg('jira-issue-terminology-refresh-m3')
												? messages.notificationColumnHeaderAllIssuesIssueTermRefresh
												: messages.notificationColumnHeaderAllIssues,
										)}
									</HeadCellContainer>
									<HeadCellCheckboxContainer>
										{allPreferences && (
											<HeadCheckbox
												id={`all-issues-${inProductProjectHeadCheckboxId}`}
												commitMutation={projectHeadMutationInProduct}
												channel={IN_PRODUCT}
												label={formatMessage(messages.inProductColumnHeader)}
												allPreferences={allPreferences}
												enabledDataFragment={enabledPreference.jira.notificationGlobalPreference}
												categories={['ISSUE_ASSIGNED', 'ISSUE_MENTIONED']}
											/>
										)}
									</HeadCellCheckboxContainer>
									<HeadCellCheckboxContainer>
										{allPreferences && (
											<HeadCheckbox
												id={`all-issues-${emailProjectHeadCheckboxId}`}
												commitMutation={projectHeadMutationEmail}
												channel={EMAIL}
												label={formatMessage(messages.emailColumnHeader)}
												allPreferences={allPreferences}
												enabledDataFragment={enabledPreference.jira.notificationGlobalPreference}
												categories={['ISSUE_ASSIGNED', 'ISSUE_MENTIONED']}
											/>
										)}
									</HeadCellCheckboxContainer>
								</TableHeadRowContainer>
							</TableHeadContainer>
							<TBody>
								<NotificationsConfigRow
									preference={allPreferences.issueAssigned}
									allPreferences={allPreferences}
									label={formatMessage(
										fg('jira-issue-terminology-refresh-m3')
											? messages.issueAssignedTypeIssueTermRefresh
											: messages.issueAssignedType,
									)}
									id={`issueAssigned-${projectId}`}
									commitMutation={projectRowMutation}
									headCheckboxIds={headCheckboxIds}
									enabledDataFragment={enabledPreference.jira.notificationGlobalPreference}
								/>
								<NotificationsConfigRow
									preference={allPreferences.mentionsCombined}
									allPreferences={allPreferences}
									label={formatMessage(
										fg('jira-issue-terminology-refresh-m3')
											? messages.mentionsCombinedTypeIssueTermRefresh
											: messages.mentionsCombinedType,
									)}
									id={`mentionsCombined-${projectId}`}
									commitMutation={projectRowMutation}
									headCheckboxIds={headCheckboxIds}
									enabledDataFragment={enabledPreference.jira.notificationGlobalPreference}
								/>
							</TBody>
						</Table>
					</Box>
					<Box xcss={tableWrapperStyles}>
						<Table>
							<TableHeadContainer>
								<TableHeadRowContainer>
									<HeadCellContainer>
										<Inline alignBlock="center">
											{formatMessage(
												fg('jira-issue-terminology-refresh-m3')
													? messages.notificationColumnHeaderRelevantIssuesIssueTermRefresh
													: messages.notificationColumnHeaderRelevantIssues,
											)}
											<Popup
												placement="right-start"
												messageId="personal-settings-notifications-config-table.ui.project-notifications-config.project-notifications-config-table.popup"
												messageType="transactional"
												isOpen={isInfoPanelOpen}
												onClose={() => {
													setIsInfoPanelOpen(false);
												}}
												content={() => (
													<Box padding="space.200">
														{formatMessage(
															fg('jira-issue-terminology-refresh-m3')
																? messages.relevantIssueNotificationTableInfoIconTextIssueTermRefresh
																: messages.relevantIssueNotificationTableInfoIconText,
														)}
													</Box>
												)}
												trigger={({ 'aria-expanded': _, ...triggerProps }) => (
													<Box
														xcss={infoIconStyles}
														onMouseEnter={() => {
															setIsInfoPanelOpen(true);
														}}
														onMouseLeave={() => {
															setIsInfoPanelOpen(false);
														}}
														testId="personal-settings-notifications-config-table.ui.project-notifications-config.project-notifications-config-table.warning-pop-up-button-container"
														// eslint-disable-next-line react/jsx-props-no-spreading
														{...triggerProps}
													>
														<EditorPanelIcon
															spacing="spacious"
															label="warning"
															color={token('color.icon.accent.gray')}
														/>
													</Box>
												)}
											/>
										</Inline>
									</HeadCellContainer>
									<HeadCellCheckboxContainer>
										{allPreferences && (
											<HeadCheckbox
												id={`relevant-issues-${inProductProjectHeadCheckboxId}`}
												commitMutation={projectHeadMutationInProduct}
												channel={IN_PRODUCT}
												label={formatMessage(messages.inProductColumnHeader)}
												allPreferences={allPreferences}
												enabledDataFragment={enabledPreference.jira.notificationGlobalPreference}
												categories={[
													'ISSUE_CHANGES',
													'COMMENT_CHANGES',
													'ISSUE_WORKLOG_CHANGES',
													'ISSUE_MISCELLANEOUS',
												]}
											/>
										)}
									</HeadCellCheckboxContainer>
									<HeadCellCheckboxContainer>
										{allPreferences && (
											<HeadCheckbox
												id={`relevant-issues-${emailProjectHeadCheckboxId}`}
												commitMutation={projectHeadMutationEmail}
												channel={EMAIL}
												label={formatMessage(messages.emailColumnHeader)}
												allPreferences={allPreferences}
												enabledDataFragment={enabledPreference.jira.notificationGlobalPreference}
												categories={[
													'ISSUE_CHANGES',
													'COMMENT_CHANGES',
													'ISSUE_WORKLOG_CHANGES',
													'ISSUE_MISCELLANEOUS',
												]}
											/>
										)}
									</HeadCellCheckboxContainer>
								</TableHeadRowContainer>
							</TableHeadContainer>
							<TBody>
								<NotificationsConfigRowAccordion
									allPreferences={allPreferences}
									label={formatMessage(
										fg('jira-issue-terminology-refresh-m3')
											? messages.issueChangesCategoryTypeIssueTermRefresh
											: messages.issueChangesCategoryType,
									)}
									id={`issueChanges-${projectId}`}
									onRowMutation={projectRowMutation}
									headCheckboxIds={headCheckboxIds}
									enabledDataFragment={enabledPreference.jira.notificationGlobalPreference}
									onEmailHeadMutation={projectHeadMutationEmail}
									onInProductHeadMutation={projectHeadMutationInProduct}
									category="ISSUE_CHANGES"
									projectId={projectId}
								/>
								<NotificationsConfigRowAccordion
									allPreferences={allPreferences}
									label={formatMessage(
										fg('jira-issue-terminology-refresh-m3')
											? messages.commentChangesCategoryTypeIssueTermRefresh
											: messages.commentChangesCategoryType,
									)}
									id={`commentChanges-${projectId}`}
									onRowMutation={projectRowMutation}
									headCheckboxIds={headCheckboxIds}
									enabledDataFragment={enabledPreference.jira.notificationGlobalPreference}
									onEmailHeadMutation={projectHeadMutationEmail}
									onInProductHeadMutation={projectHeadMutationInProduct}
									category="COMMENT_CHANGES"
									projectId={projectId}
								/>
								{allPreferences.worklogCombined && (
									<NotificationsConfigRow
										preference={allPreferences.worklogCombined}
										allPreferences={allPreferences}
										label={formatMessage(messages.worklogCombinedType)}
										id={`worklogCombined-${projectId}`}
										commitMutation={projectRowMutation}
										headCheckboxIds={headCheckboxIds}
										enabledDataFragment={enabledPreference.jira.notificationGlobalPreference}
									/>
								)}
								{allPreferences.miscellaneousIssueEventCombined && (
									<NotificationsConfigRow
										preference={allPreferences.miscellaneousIssueEventCombined}
										allPreferences={allPreferences}
										label={formatMessage(
											fg('jira-issue-terminology-refresh-m3')
												? messages.miscellaneousIssueEventCombinedIssueTermRefresh
												: messages.miscellaneousIssueEventCombined,
										)}
										id={`miscellaneousIssueEventCombined-${projectId}`}
										commitMutation={projectRowMutation}
										headCheckboxIds={headCheckboxIds}
										enabledDataFragment={enabledPreference.jira.notificationGlobalPreference}
									/>
								)}
							</TBody>
						</Table>
					</Box>
					<Box xcss={removeButtonStyles}>
						<RemoveButton project={projectPreferences} />
					</Box>
				</ProjectNotificationsAccordion>
			</Box>
		</ContextualAnalyticsData>
	);
};

const removeButtonStyles = xcss({
	marginTop: 'space.200',
});

const projectContainerStyles = xcss({
	marginTop: 'space.150',
});

const infoIconStyles = xcss({
	marginLeft: 'space.100',
	marginTop: 'space.025',
});

const tableWrapperStyles = xcss({
	borderWidth: 'border.width',
	borderStyle: 'solid',
	borderColor: 'color.border',
	backgroundColor: 'elevation.surface.raised',
	borderRadius: 'border.radius.050',
	marginBottom: 'space.200',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':last-of-type': {
		marginBottom: 'space.0',
	},
});
